import { ChangeEvent } from "react"
import { FileText, Upload } from "lucide-react"

import { Button } from "@f2e/ui/components/base"

import useDragAndDrop from "@/hooks/useDragAndDrop"

export interface UploadDropzoneProps {
  file: File | null
  onFilesSelected: (files: File[]) => void
  accept?: string
  selectFileLabel: string
  supportedFormatLabel: string
  chooseFileLabel: string
  rechooseFileLabel: string
  disabled?: boolean
  className?: string
}

const UploadDropzone = ({
  file,
  onFilesSelected,
  accept = ".csv",
  selectFileLabel,
  supportedFormatLabel,
  chooseFileLabel,
  rechooseFileLabel,
  disabled = false,
  className,
}: UploadDropzoneProps) => {
  const { isDragging, dragHandlers } = useDragAndDrop({
    onFilesDropped: disabled ? () => undefined : onFilesSelected,
    logPrefix: "UploadDropzone",
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const picked = Array.from(e.target.files ?? [])
    e.target.value = ""
    if (picked.length > 0) onFilesSelected(picked)
  }

  const hiddenInput = (
    <input
      type="file"
      accept={accept}
      disabled={disabled}
      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      onChange={handleChange}
    />
  )

  if (file) {
    return (
      <div
        className={`rounded-lg border bg-primary/5 border-primary/40 p-4 flex items-center justify-between ${className ?? ""}`}
      >
        <div className="flex items-center gap-2 text-sm font-medium min-w-0">
          <FileText className="h-5 w-5 text-primary shrink-0" />
          <span className="truncate">{file.name}</span>
        </div>
        <label
          className={`relative shrink-0 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
        >
          <Button type="button" variant="outline" size="sm" asChild>
            <span>{rechooseFileLabel}</span>
          </Button>
          {hiddenInput}
        </label>
      </div>
    )
  }

  return (
    <label
      className={`relative flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
        disabled
          ? "cursor-not-allowed border-muted-foreground/20 opacity-60"
          : isDragging
            ? "border-primary bg-primary/10"
            : "border-muted-foreground/25 hover:border-muted-foreground/50"
      } ${className ?? ""}`}
      {...(disabled ? {} : dragHandlers)}
    >
      <Upload
        className={`h-10 w-10 mb-3 ${isDragging ? "text-primary" : "text-muted-foreground"}`}
      />
      <p className="text-sm font-medium mb-1">{selectFileLabel}</p>
      <p className="text-xs text-muted-foreground mb-4">
        {supportedFormatLabel}
      </p>
      <Button type="button" variant="outline" size="sm" asChild>
        <span>
          <Upload className="h-4 w-4 mr-2" />
          {chooseFileLabel}
        </span>
      </Button>
      {hiddenInput}
    </label>
  )
}

export default UploadDropzone
